import {isDemo} from "../tools/CommonHtml";
import {makeArr, MS_DAY, objRmKeysC} from "../tools/CommonJS";

// Deterministic hash → float 0..1 so injection decisions are stable across re-renders/refetches.
const stableRand = (str) => {
	let h = 5381;
	for (let i = 0; i < str.length; i++) h = ((h * 33) ^ str.charCodeAt(i)) >>> 0;
	return (h % 1000) / 1000;
};

// ─── remediable overlays ────────────────────────────────────────────────────

const injectRemediable = (item, key, badCount, always) => {
	if (item.remediable) return item;
	if (!always && stableRand(key) >= 0.3) return item;
	return {...item, remediable: Math.max(1, badCount || 1)};
};

// The demo backend never sets mutedAlerts, so inject a stable subset (~40%, matching the local mock) with a
// 1..12 count so the "Muted" status bubble shows on the patch list and Top Missing card.
const injectMuted = (item, key) => {
	if (item.mutedAlerts) return item;
	const r = stableRand(`mute_${key}`);
	return r < 0.4 ? {...item, mutedAlerts: 1 + Math.floor(r * 30)} : item;
};

// The demo backend predates the patch action's `patchable` flag. Phase 3 installs Windows OS patches only; the caller
// decides whether a row is actionable (list: Windows row with unpatched devices; drilldown: device of a Windows KB).
const injectPatchable = (item, actionable) => "patchable" in item ? item : {...item, patchable: !!actionable};

// The demo backend never returns knownIssues, so attach the sample to the 2nd and 3rd rows of each patch list
// so the known-issues warning shows on the patch lists and Top Missing card regardless of which KBs come back.
const PATCH_KNOWN_ISSUES = [
	{title: "Some devices might fail to shut down or hibernate",
		osBuild: "OS Build 20348.4648",
		originatingKb: "KB5073457",
		originatingDate: "2026-01-13",
		description: "This issue affects some Secure Launch-capable PCs with Virtual Secure Mode enabled after installing Jan '26 updates.",
		status: "Resolved",
		resolvedKb: "KB5075906",
		resolvedDate: "2026-02-10 10:00 PT"},
	{title: "Apps might become unresponsive when saving files to cloud-backed storage",
		osBuild: "OS Build 20348.4648",
		originatingKb: "KB5073457",
		originatingDate: "2026-01-13",
		description: "Affected apps include Outlook, which can become unresponsive when accessing PST files stored on Microsoft OneDrive.",
		status: "Resolved",
		resolvedKb: "KB5078136",
		resolvedDate: "2026-01-23 14:00 PT"},
	{title: "Connection and authentication failures in Azure Virtual Desktop and Windows 365",
		osBuild: "OS Build 20348.4648",
		originatingKb: "KB5073457",
		originatingDate: "2026-01-13",
		description: "January 2026 Windows update causes Windows App credential prompt failures on Azure Virtual Desktop and Windows 365.",
		status: "Resolved",
		resolvedKb: "KB5077800",
		resolvedDate: "2026-01-17 14:00 PT"}
];

const injectKnownIssues = (item, idx) => {
	if (item.knownIssues) return item;
	if (idx === 1) return {...item, knownIssues: PATCH_KNOWN_ISSUES};
	if (idx === 2) return {...item, knownIssues: [PATCH_KNOWN_ISSUES[0]]};
	return item;
};

const overlayList = (data, keyFn, badFn = () => 1, always = false, extra) => {
	if (!Array.isArray(data?.data)) return data;
	return {...data,
		data: data.data.map((item, idx) => {
			const withRem = injectRemediable(item, keyFn(item), badFn(item), always);
			return extra ? extra(withRem, keyFn(item), idx) : withRem;
		})};
};

// ─── action log: patch management overlay ───────────────────────────────────

const PATCH_KBS = ["KB5066131", "KB5034441", "KB5022913", "KB5025224", "KB5028997"];

// Applied OS patches all carry the fixed actionDatum; the specific KB is identified by topicCode / ruleName (see makePatchCriteria).
const patchActionDatum = () => "download-and-install-os-patch";

const makePatchCriteria = (ruleId) => {
	const kb = PATCH_KBS[ruleId % PATCH_KBS.length];
	return {
		actionType: "remediate",
		actionDatum: patchActionDatum(ruleId),
		ruleName: `Install ${kb}`,
		by: "devices",
		topicCode: kb,
		benchmarkOrg: "OS_PATCH_MANAGEMENT",
		list: [],
		listParamExtra: [],
		isListExclude: true,
	};
};

const makePatchActionEntry = (item) => ({
	...item,
	actionType: "remediate",
	actionDatum: patchActionDatum(item.ruleId),
	criteria: makePatchCriteria(item.ruleId),
});

const PATCH_ACTION_FALLBACK = [
	{ruleId: 98001,
		state: "active",
		actionType: "remediate",
		actionDatum: patchActionDatum(0),
		errorString: null,
		errorDate: null,
		remark: null,
		latestActivity: "2026-06-20T10:00:00Z",
		taskStatusCounts: {pending: 0, success: 5, reverting: 0, reverted: 0, cancelled: 0, failed: 0, revertFailed: 0, revertCancelled: 0},
		canRevert: false,
		user: "demo@remedio.io",
		applyScheme: "asap",
		afterTime: 0,
		timezone: 0,
		weekDay: null,
		window: 0,
		criteria: makePatchCriteria(0),
		log: [{byUser: {user: "demo@remedio.io", userName: "Demo User", title: ""}, date: "2026-06-20T10:00:00Z", operation: "create", notes: null}],
		approvalRequestId: null,
		approvalRequestIdExtra: null},
	{ruleId: 98002,
		state: "done",
		actionType: "remediate",
		actionDatum: patchActionDatum(1),
		errorString: null,
		errorDate: null,
		remark: null,
		latestActivity: "2026-06-15T08:30:00Z",
		taskStatusCounts: {pending: 0, success: 12, reverting: 0, reverted: 0, cancelled: 1, failed: 0, revertFailed: 0, revertCancelled: 0},
		canRevert: false,
		user: "demo@remedio.io",
		applyScheme: "asap",
		afterTime: 0,
		timezone: 0,
		weekDay: null,
		window: 0,
		criteria: makePatchCriteria(1),
		log: [{byUser: {user: "demo@remedio.io", userName: "Demo User", title: ""}, date: "2026-06-15T08:30:00Z", operation: "create", notes: null}],
		approvalRequestId: null,
		approvalRequestIdExtra: null},
	{ruleId: 98003,
		state: "active",
		actionType: "remediate",
		actionDatum: patchActionDatum(2),
		errorString: null,
		errorDate: null,
		remark: null,
		latestActivity: "2026-06-10T14:00:00Z",
		taskStatusCounts: {pending: 3, success: 8, reverting: 0, reverted: 0, cancelled: 0, failed: 1, revertFailed: 0, revertCancelled: 0},
		canRevert: false,
		user: "demo@remedio.io",
		applyScheme: "nextWeekDay",
		afterTime: 0,
		timezone: 0,
		weekDay: 1,
		window: 0,
		criteria: makePatchCriteria(2),
		log: [{byUser: {user: "demo@remedio.io", userName: "Demo User", title: ""}, date: "2026-06-10T14:00:00Z", operation: "create", notes: null}],
		approvalRequestId: null,
		approvalRequestIdExtra: null},
	{ruleId: 98004,
		state: "done",
		actionType: "remediate",
		actionDatum: patchActionDatum(3),
		errorString: null,
		errorDate: null,
		remark: null,
		latestActivity: "2026-06-05T09:15:00Z",
		taskStatusCounts: {pending: 0, success: 20, reverting: 0, reverted: 2, cancelled: 0, failed: 0, revertFailed: 0, revertCancelled: 0},
		canRevert: false,
		user: "demo@remedio.io",
		applyScheme: "asap",
		afterTime: 0,
		timezone: 0,
		weekDay: null,
		window: 0,
		criteria: makePatchCriteria(3),
		log: [{byUser: {user: "demo@remedio.io", userName: "Demo User", title: ""}, date: "2026-06-05T09:15:00Z", operation: "create", notes: null}],
		approvalRequestId: null,
		approvalRequestIdExtra: null},
	{ruleId: 98005,
		state: "active",
		actionType: "remediate",
		actionDatum: patchActionDatum(4),
		errorString: null,
		errorDate: null,
		remark: null,
		latestActivity: "2026-06-28T16:45:00Z",
		taskStatusCounts: {pending: 6, success: 2, reverting: 0, reverted: 0, cancelled: 0, failed: 2, revertFailed: 0, revertCancelled: 0},
		canRevert: false,
		user: "demo@remedio.io",
		applyScheme: "afterTime",
		afterTime: 0,
		timezone: 0,
		weekDay: null,
		window: 4,
		criteria: makePatchCriteria(4),
		log: [{byUser: {user: "demo@remedio.io", userName: "Demo User", title: ""}, date: "2026-06-28T16:45:00Z", operation: "create", notes: null}],
		approvalRequestId: null,
		approvalRequestIdExtra: null}
];

// The Source filter sends an EXCLUDE array (filterModulesDefs[val].exclude), not a module name.
// Mirrors filterModulesDefs.patch_management.exclude — duplicated rather than imported because importing ActionData
// here closes a cycle (AppContext → DemoOverlays → ActionData → … → AppContext) that breaks module init.
// Drift is safe in both directions: a module added to every exclude list keeps Patches matching and still rejects the others.
const PATCH_MGMT_EXCLUDE = ["miscon", "app_control", "benchmark", "app_vuln_assessment", "patch_vuln_assessment", "customcis"];

// Match the Patches selection by its actual exclude set, not by "non-empty and omits patch_management" — other
// selections also carry non-empty excludes (e.g. "Misc. / Generic" excludes network_scanner), so the looser test
// misidentified them as Patch Mgmt. and rewrote their rows into synthetic patch actions.
const isPatchMgmtContext = (params) => {
	const mods = params?.modules;
	const isPatch = mods === "patch_management" ||
        (Array.isArray(mods) && mods.length > 0 && !mods.includes("patch_management")
            && PATCH_MGMT_EXCLUDE.every(m => mods.includes(m)));
	return isPatch && params?.actionTypes?.includes?.("remediate");
};

// ─── patch/vuln check-remediate overlay ──────────────────────────────────────

// APP_CONTROL is deliberately absent: its only action config is the Uninstall dialog, so a synthetic "Update to
// latest" patch remedy would render an update card under an "Uninstall" title. Re-add it only alongside a
// dedicated App Control patch flow (patchCheckRsp's isAppControl branch is kept ready for that).
const PATCH_VULN_ORGS = new Set(["APP_VULN_ASSESSMENT", "PATCH_VULN_ASSESSMENT", "OS_PATCH_MANAGEMENT"]);

const patchCheckRsp = (params) => {
	const org = params?.actions?.[0]?.benchmarkOrg;
	if (!PATCH_VULN_ORGS.has(org)) return null;
	const isOs = org !== "APP_VULN_ASSESSMENT" && org !== "APP_CONTROL";
	const isAppControl = org === "APP_CONTROL";
	let remedy;
	if (isOs)
        // OS patch + CVE patch share the fixed `download-and-install-os-patch` actionDatum; the specific KB rides on topicCode.
		remedy = {actionDatum: "download-and-install-os-patch",
			kind: "os",
			title: "Install KB5022913",
			product: "Windows Kernel",
			currentVersion: "22H2",
			targetVersion: "23H2",
			patchType: "Cumulative Update",
			source: "Microsoft Update",
			rebootRequired: true};
	else if (isAppControl) {
		const product = params?.actions?.[0]?.topicCode ?? "Application";
		const slug = product.replace(/\W+/g, "_").toLowerCase().slice(0, 30);
		remedy = {actionDatum: `patch_${slug}_latest`,
			kind: "app",
			title: `Update ${product} to latest version`,
			product,
			currentVersion: "1.0.0",
			targetVersion: "2.0.0",
			packageName: `${slug}-setup.msi`,
			source: "Vendor",
			rebootRequired: false};
	} else
		remedy = {actionDatum: "patch_7zip_x64_25_01",
			kind: "app",
			title: "Install 7-Zip (x64) v25.01",
			product: "7-Zip (x64)",
			currentVersion: "9.1.0",
			targetVersion: "25.01",
			packageName: "7zip-x64-25.01.msi",
			source: "Vendor (7-zip.org)",
			rebootRequired: false};
	return {
		applicAlerts: isOs ? 2 : 1,
		applicDevices: isOs ? 3 : 1,
		applicNonRevert: 0,
		notApplic: [],
		remedies: [remedy],
		remediesAvailable: [remedy.actionDatum],
		selection: {instances: isOs ? 3 : 1, versionFrom: isOs ? "21H2" : remedy.currentVersion, versionTo: isOs ? "22H2" : remedy.currentVersion},
		approvalService: "",
		approvalServiceError: "",
	};
};

// ─── patch dashboard overlays ────────────────────────────────────────────────
// The demo backend returns real Patch Tuesday / insights data but omits some cards' data: the two GRAPHICAL
// cards (no breakdown, and a near-flat unpatchedSeries) and the "Most Relevant to Your Fleet" card (no
// patchTuesday.mostRelevant). Backfill just those so the dashboard looks complete, leaving every real field
// untouched (backend values win where present). Generated here (not pulled from mockData) because mockData is
// gated behind VITE_ENV==="dev" and is absent on demo builds.

// "Most Relevant to Your Fleet" — matches the local mock so demo and dev read the same.
const PATCH_MOST_RELEVANT = {kbId: 5060999, title: "2026-06 Cumulative Update for Windows 11 23H2", devicesAffected: 60};

const PATCH_BREAKDOWN = [
	{osFamily: "Windows",
		data: [
			{category: "Security Updates", patchedDevices: 150, unpatchedDevices: 850},   // 15% - red.
			{category: "Updates", patchedDevices: 270, unpatchedDevices: 630},            // 30% - red.
			{category: "", patchedDevices: 45, unpatchedDevices: 55},                     // 45% - orange.
			{category: "Definition Updates", patchedDevices: 75, unpatchedDevices: 25},   // 75% - yellow.
			{category: "Update Rollups", patchedDevices: 95, unpatchedDevices: 5}         // 95% - green.
		]},
	{osFamily: "Linux",
		data: [
			{category: "", patchedDevices: 25, unpatchedDevices: 75},                     // 25% - red.
			{category: "Updates", patchedDevices: 55, unpatchedDevices: 45},              // 55% - orange.
			{category: "Security Updates", patchedDevices: 85, unpatchedDevices: 15}      // 85% - yellow.
		]},
	{osFamily: "macOS",
		data: [
			{category: "Updates", patchedDevices: 40, unpatchedDevices: 60},              // 40% - orange.
			{category: "Security Updates", patchedDevices: 88, unpatchedDevices: 12}      // 88% - yellow.
		]},
	{osFamily: "Applications",
		data: [                                                 // Demo-only scope; categories mirror PATCH_APP_ROWS.
			{category: "Security Updates", patchedDevices: 210, unpatchedDevices: 122},   // 63% - yellow.
			{category: "Application Updates", patchedDevices: 12, unpatchedDevices: 45}   // 21% - red.
		]}
];

// Distinct shapes per scope: Windows climbs with big swings, Linux declines (remediation), macOS is small and spiky, Applications drifts down slowly.
const patchSeriesDefs = {
	Windows: {base: 320, slope: 7, amp: 110, freq: 3},
	Linux: {base: 260, slope: -5, amp: 70, freq: 5},
	macOS: {base: 45, slope: 2, amp: 30, freq: 2.2},
	Applications: {base: 180, slope: -3, amp: 45, freq: 4}
};

// 30-day per-OS backlog curve; deterministic (no random noise) so it stays stable across refetches.
const patchUnpatchedSeries = () => Object.entries(patchSeriesDefs).map(([osFamily, {
	base, slope, amp, freq
}]) => ({
	osFamily,
	data: makeArr(30).map((_, i) => ({
		date: new Date(Date.now() - (29 - i) * MS_DAY).toISOString(),
		deviceCount: Math.max(0, Math.round(base + Math.sin(i / freq) * amp + i * slope))
	}))
}));

// The demo backend knows nothing of the cross-OS "Applications" bucket: serve its patch rows and insights
// entirely from here (matching the local mock) so the Applications tab and its cards demo like the OS ones.
const PATCH_APP_ROWS = [
	{topicCode: "Google Chrome", title: "Google Chrome", productName: "Google Chrome", vendor: "Google Inc.", version: "138.0.7204.97", severity: "Critical", cves: ["CVE-2026-6558", "CVE-2026-6601", "CVE-2026-6603"], cveCount: 7, os: ["Win 11 Enterprise", "macOS 15.2"], osFamily: "Applications", description: "Update Google Chrome to 138.0.7204.97 — fixes 7 vulnerabilities including a V8 type confusion exploited in the wild.", category: "Security Updates", rebootRequired: false, publishDate: "2026-06-24T00:00:00Z", devicesOk: 210, devicesBad: 122, remediable: 122, mutedAlerts: 4},
	{topicCode: "Microsoft Edge", title: "Microsoft Edge", productName: "Microsoft Edge", vendor: "Microsoft Corporation", version: "137.0.3296.62", severity: "Critical", cves: ["CVE-2026-6710", "CVE-2026-6712"], cveCount: 3, os: ["Win 10 Enterprise", "Win 11 Enterprise"], osFamily: "Applications", description: "Update Microsoft Edge to 137.0.3296.62 — inherited Chromium security fixes.", category: "Security Updates", rebootRequired: false, publishDate: "2026-06-26T00:00:00Z", devicesOk: 340, devicesBad: 85, remediable: 85, mutedAlerts: 0},
	{topicCode: "Claude Desktop", title: "Claude Desktop", productName: "Claude Desktop", vendor: "Anthropic", version: "1.8.2", severity: "Medium", cves: [], cveCount: 0, os: ["Win 11 Enterprise", "macOS 15.2"], osFamily: "Applications", description: "Update Claude Desktop to 1.8.2 — Electron runtime security update.", category: "Application Updates", rebootRequired: false, publishDate: "2026-06-18T00:00:00Z", devicesOk: 12, devicesBad: 45, remediable: 45, mutedAlerts: 0},
	{topicCode: "Notepad++", title: "Notepad++", productName: "Notepad++", vendor: "Notepad++ Team", version: "8.8.5", severity: "Medium", cves: ["CVE-2025-49144"], cveCount: 1, os: ["Win 10 Enterprise", "Win 11 Enterprise"], osFamily: "Applications", description: "Update Notepad++ to 8.8.5 — fixes installer binary-planting privilege escalation.", category: "Security Updates", rebootRequired: false, publishDate: "2026-06-10T00:00:00Z", devicesOk: 26, devicesBad: 41, remediable: 41, mutedAlerts: 1},
	{topicCode: "Python", title: "Python", productName: "Python", vendor: "Python Software Foundation", version: "3.14.4", severity: "Low", cves: ["CVE-2026-1148"], cveCount: 2, os: ["Win 11 Enterprise", "Ubuntu 22.04"], osFamily: "Applications", description: "Update Python to 3.14.4 — tarfile path-traversal hardening.", category: "Security Updates", rebootRequired: false, publishDate: "2026-06-03T00:00:00Z", devicesOk: 88, devicesBad: 9, remediable: 9, mutedAlerts: 0}
];

const PATCH_APP_INSIGHTS = {osFamily: "Applications", data: {deviceExposures: 302, missingPatches: 57, highestRisk: {topicCode: "Google Chrome", cvss: 9.6, devicesAffected: 122}, overdueCount: 9}};

const patchAppListRsp = () => ({rowCount: PATCH_APP_ROWS.length, calcState: 0, data: PATCH_APP_ROWS});

const overlayPatchDashboard = (d) => {
	if (!d || d.error) return d;
	return {
		...d,
		patchTuesday: d.patchTuesday && {...d.patchTuesday, mostRelevant: d.patchTuesday.mostRelevant ?? PATCH_MOST_RELEVANT},
		insights: d.insights?.some(i => i.osFamily === "Applications") ? d.insights : [...(d.insights ?? []), PATCH_APP_INSIGHTS],
		breakdown: PATCH_BREAKDOWN,
		unpatchedSeries: patchUnpatchedSeries()
	};
};

// ─── overlay map ─────────────────────────────────────────────────────────────

const overlays = {
	"/api/v2/patch-management":           (d, params) => params?.osFamily === "Applications" ? patchAppListRsp()
		: overlayList(d, i => i.topicCode, i => i.devicesBad, true, (i, k, idx) => injectKnownIssues(injectMuted(injectPatchable(i, i.osFamily === "Windows" && i.devicesBad), k), idx)),
	"/api/v2/patch-management_instances": (d) => overlayList(d, i => i.osVersion + (i.param ?? ""), i => i.badDevices, true),
    // Device drilldown of a specific KB — Windows KBs (topicCode "KB…") are patchable; Linux/macOS package drilldowns stay visibility-only.
	"/api/v2/patch-management_devices":   (d, params) => overlayList(d, i => String(i.deviceId), () => 1, true, i => injectPatchable(i, params?.topicCode?.startsWith("KB") && i.alertsBad)),
	"/api/v2/app-control":                (d) => overlayList(d, i => i.topicCode,                   i => i.devicesBad),
	"/api/v2/app-control_instances":      (d) => overlayList(d, i => i.param,                       i => i.badDevices),
	"/api/v2/app-control_devices":        (d) => overlayList(d, i => String(i.deviceId)),
	"/api/v2/cves":                       (d) => overlayList(d, i => i.topicCode,                   i => i.devicesBad, true),
	"/api/v2/patch-management_dashboard": overlayPatchDashboard,
	"api/v2/topics_check-remediate-multi": (d, params) => patchCheckRsp(params) ?? d,
	"/api/v2/actions_combi-list": (d, params) => {
		if (!isPatchMgmtContext(params) || !Array.isArray(d?.data)) return d;
		if (!d.data.length)
			return {data: PATCH_ACTION_FALLBACK, rowCount: PATCH_ACTION_FALLBACK.length};
		return {
			...d,
			data: d.data.map(item => stableRand(String(item.ruleId)) < 0.6 ? makePatchActionEntry(item) : item),
		};
	},
};

// ─── param overlay map (request side) ────────────────────────────────────────

// The demo backend predates the app-control "patchable" flag and 400s on it — strip it (the filter then demos as a no-op).
const stripPatchableFlag = (p) => p.flags?.includes("patchable") ? {...p, flags: p.flags.filter(f => f !== "patchable")} : p;

const paramOverlays = {
    // Strip action types the demo server doesn't recognise yet.
	"/api/v2/actions_combi-list": (p) => ({
		...p,
		actionTypes: p.actionTypes?.filter(t => t !== "netscanScan"),
	}),
	"/api/v2/app-control": stripPatchableFlag,
	"/api/v2/miscon_combi-field": stripPatchableFlag,
    // The demo backend rejects the cross-OS "Applications" bucket ("invalid os") — strip it so the request
    // returns 200; the response overlay (keyed on the ORIGINAL params) then swaps in PATCH_APP_ROWS.
	"/api/v2/patch-management": (p) => p.osFamily === "Applications" ? {...p, osFamily: undefined} : p,
    // The demo backend only accepts by ∈ {"", "devices", "alerts"} and 400s on the patch/app-control
    // by:"topicCode" selection. Rewrite it to an all-devices filter so the request returns 200; the response
    // overlay (patchCheckRsp, keyed on the ORIGINAL params) then swaps in the synthetic patch-check result.
	"api/v2/topics_check-remediate-multi": (p) => !Array.isArray(p.actions) ? p : {
		...p,
		actions: p.actions.map(a => a.by !== "topicCode" ? a
			: {...objRmKeysC(a, "list", "listParam", "listParamExtra", "listIds", "listGuids", "topicCode"), by: "devices", isListExclude: true}),
	},
};

// ─── exports ─────────────────────────────────────────────────────────────────

export const applyDemoParamOverlay = (api, params) => {
	if (!isDemo || !params) return params;
	return paramOverlays[api]?.(params) ?? params;
};

export const applyDemoOverlay = (api, data, params) => {
	if (!isDemo) return data;
	return overlays[api]?.(data, params) ?? data;
};
